/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: components/auth/LogoutMenu.tsx
 * Release: v1.1.1 - Solo superadmin vede logout completo
 * 
 * Fix per problema menu tagliato dal bordo finestra
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, LogOut, RotateCcw } from 'lucide-react';
import { ConfirmModal } from '../ConfirmModal'; // Quello con variant support
import { useAuthActions } from './useAuthActions';

interface LogoutMenuProps {
  onLogout?: () => void;
  onCompleteLogout?: () => void;
  isSuperAdmin?: boolean;
}

export const LogoutMenu: React.FC<LogoutMenuProps> = ({ isSuperAdmin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [logoutType, setLogoutType] = useState<'normal' | 'complete'>('normal');
  const [position, setPosition] = useState<'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'>('bottom-left');
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { signOut, forceSignOut } = useAuthActions();

  // Calcola la posizione ottimale del menu per evitare il taglio
  const calculateMenuPosition = () => {
    if (!buttonRef.current || !menuRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let newPosition: typeof position = 'bottom-left';

    // Controlla se il menu esce dai bordi orizzontali
    const wouldOverflowRight = buttonRect.left + menuRect.width > viewportWidth - 20;
    const wouldOverflowBottom = buttonRect.bottom + menuRect.height > viewportHeight - 20;

    if (wouldOverflowRight && wouldOverflowBottom) {
      newPosition = 'top-right';
    } else if (wouldOverflowRight) {
      newPosition = 'bottom-right';
    } else if (wouldOverflowBottom) {
      newPosition = 'top-left';
    } else {
      newPosition = 'bottom-left';
    }

    setPosition(newPosition);
  };

  // Calcola posizione quando il menu si apre
  useEffect(() => {
    if (isOpen) {
      // Aspetta un frame per il rendering del menu
      requestAnimationFrame(() => {
        calculateMenuPosition();
      });
    }
  }, [isOpen]);

  // Chiudi il menu cliccando fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Riposiziona il menu al resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculateMenuPosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const getMenuClasses = () => {
    const baseClasses = "absolute z-50 bg-gray-800 border border-gray-600 rounded-lg shadow-lg w-48 py-2";
    
    switch (position) {
      case 'bottom-left':
        return `${baseClasses} top-full left-0 mt-2`;
      case 'bottom-right':
        return `${baseClasses} top-full right-0 mt-2`;
      case 'top-left':
        return `${baseClasses} bottom-full left-0 mb-2`;
      case 'top-right':
        return `${baseClasses} bottom-full right-0 mb-2`;
      default:
        return `${baseClasses} top-full left-0 mt-2`;
    }
  };

  const handleLogoutClick = (type: 'normal' | 'complete') => {
    setLogoutType(type);
    setShowConfirmModal(true);
    setIsOpen(false);
  };

  const handleConfirmLogout = React.useCallback(async () => {
    console.log('🔐🔐🔐 [LogoutMenu] ===== handleConfirmLogout CHIAMATO! =====');
    console.log('🔐🔐🔐 [LogoutMenu] Tipo logout:', logoutType);

    try {
      if (logoutType === 'complete') {
        console.log('🔐🔐🔐 [LogoutMenu] Chiamando forceSignOut...');
        await forceSignOut();
        console.log('🔐🔐🔐 [LogoutMenu] forceSignOut completato');
      } else {
        console.log('🔐🔐🔐 [LogoutMenu] Chiamando signOut...');
        await signOut();
        console.log('🔐🔐🔐 [LogoutMenu] signOut completato');
      }
    } catch (error) {
      console.error('🔐🔐🔐 [LogoutMenu] Errore durante logout:', error);
    }

    console.log('🔐🔐🔐 [LogoutMenu] ===== handleConfirmLogout COMPLETATO =====');
  }, [logoutType, forceSignOut, signOut]);

  // Log quando viene passata la funzione al ConfirmModal
  React.useEffect(() => {
    if (showConfirmModal) {
      console.log('🔐🔐🔐 [LogoutMenu] Rendering ConfirmModal - onConfirm function:', handleConfirmLogout);
    }
  }, [showConfirmModal, handleConfirmLogout]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 px-3 py-2 rounded-lg transition-colors"
      >
        <LogOut className="h-4 w-4" />
        <span className="text-sm font-medium">Logout</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div ref={menuRef} className={getMenuClasses()}>
          <div className="px-3 py-2 border-b border-gray-600">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Opzioni Logout
            </div>
          </div>
          
          <button
            onClick={() => handleLogoutClick('normal')}
            className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            <div>
              <div className="font-medium">Logout Normale</div>
              <div className="text-xs text-gray-500">Mantieni account Google</div>
            </div>
          </button>

          {isSuperAdmin && (
            <button
              onClick={() => handleLogoutClick('complete')}
              className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              <div>
                <div className="font-medium">Logout Completo</div>
                <div className="text-xs text-gray-500">Disconnetti anche da Google</div>
              </div>
            </button>
          )}
          
          <div className="px-3 py-2 border-t border-gray-600">
            <div className="text-xs text-gray-500">
              Usa logout completo se condividi questo computer
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmLogout}
        title={logoutType === 'complete' ? 'Conferma Logout Completo' : 'Conferma Logout'}
        message={
          logoutType === 'complete'
            ? 'Sei sicuro di voler effettuare il logout completo? Verranno rimossi tutti i dati locali (localStorage, sessionStorage, cookies) e sarai disconnesso anche da Google.'
            : 'Sei sicuro di voler uscire dall\'applicazione? Il tuo account Google resterà connesso per un accesso più rapido al prossimo login.'
        }
        confirmText={logoutType === 'complete' ? 'Conferma Logout Completo' : 'Conferma Logout'}
        variant={logoutType === 'complete' ? 'danger' : 'warning'}
      />
    </div>
  );
};